import { useCallback, useId, useRef, useState } from 'react';
import type { DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FileJson, Upload } from 'lucide-react';

import styles from './Import.module.css';

interface Props {
  onPick: (file: File) => void;
  busy: boolean;
  /** Wariant kompaktowy — pasek „wgraj poprawiony plik" wewnątrz otwartego przebiegu. */
  compact?: boolean;
  label: string;
  hint?: string;
}

/**
 * Wskazanie pliku wsadu.
 *
 * ⭐ <b>Pole i upuszczanie to JEDEN element, nie dwa.</b> Rozdzielenie ich („kliknij tu albo
 * upuść tam") daje ekran, na którym trzeba wybrać sposób, zanim zrobi się rzecz — a wybór
 * sposobu nie jest decyzją, którą ktokolwiek chce podejmować.
 *
 * ⚠ <b>Dostępność:</b> nośnikiem jest zwykły <c>&lt;input type="file"&gt;</c> pod etykietą,
 * więc klawiatura, czytnik ekranu i menedżer haseł widzą to, co zawsze. Upuszczanie jest
 * <b>dodatkiem na wierzchu</b>, nie zamiennikiem — odwrotna kolejność (własny <c>div</c>
 * z <c>role="button"</c>) wymagałaby odtwarzania zachowań, które przeglądarka ma za darmo.
 */
export function ImportDropzone({ onPick, busy, compact = false, label, hint }: Props) {
  const { t } = useTranslation();
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  const handle = useCallback(
    (file: File | undefined) => {
      if (file) onPick(file);
    },
    [onPick],
  );

  const onDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setOver(false);
      handle(event.dataTransfer.files[0]);
    },
    [handle],
  );

  return (
    <div
      className={[styles['dropzone'], compact ? styles['dropzoneCompact'] : '', over ? styles['dropzoneOver'] : '']
        .filter(Boolean)
        .join(' ')}
      onDragOver={(event) => {
        event.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      data-busy={busy ? 'true' : undefined}
    >
      <span className={styles['dropzoneIcon']} aria-hidden="true">
        {over ? <FileJson size={compact ? 18 : 28} /> : <Upload size={compact ? 18 : 28} />}
      </span>

      <div className={styles['dropzoneText']}>
        <label className={styles['dropzoneLabel']} htmlFor={inputId}>
          {label}
        </label>
        {hint ? <p className={styles['dropzoneHint']}>{hint}</p> : null}
      </div>

      <input
        ref={inputRef}
        id={inputId}
        className={styles['fileInput']}
        type="file"
        accept="application/json,.json"
        disabled={busy}
        onChange={(event) => {
          handle(event.target.files?.[0]);
          // ⚠ Czyścimy pole po wybraniu: bez tego wskazanie TEGO SAMEGO pliku drugi raz
          // (po poprawieniu go na dysku) nie wywołałoby zdarzenia — a to jest główna
          // droga wyjścia z odrzuconego wsadu (decyzja 102).
          event.target.value = '';
        }}
      />

      {busy ? (
        <output className={styles['dropzoneBusy']}>{t('import.upload.busy')}</output>
      ) : null}
    </div>
  );
}
